import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { chatActions } from 'store';
import { FindLoad, FindSuccess } from 'utils';
import { httpRequestsOnSuccessActions } from 'store';
import { CHAT_USER_TYPE } from './constants';
import { ADD_CHAT_MEMBERS, CREATE_CHAT, GET_CHAT_USERS } from '../../store/chat/chat.types';
import { CreateChancel, NoYet, SingleSearch } from '../../components';

/** `addToChat` — a group chat: the modal adds the picked people to it instead of starting a new chat. */
export const NewMessageModal = ({ open, onClose, currentUserId, currentUserType, addToChat }) => {
    const dispatch = useDispatch();
    const chatUsers = useSelector((state) => state.chat.chatUsers);
    // const chats = useSelector((state) => state.chat.chats);
    const [search, setSearch] = useState('');
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [groupName, setGroupName] = useState('');
    const searchTimeoutRef = useRef(null);
    const actionType = addToChat ? ADD_CHAT_MEMBERS : CREATE_CHAT;
    const createSuccess = FindSuccess(actionType);
    const createLoader = FindLoad(actionType);
    const usersLoading = FindLoad(GET_CHAT_USERS);

    useEffect(() => {
        if (createSuccess?.length) {
            dispatch(httpRequestsOnSuccessActions.removeSuccess(actionType));
            onClose();
            setSelectedUsers([]);
            setGroupName('');
            setSearch('');
        }
    }, [createSuccess]);

    useEffect(() => {
        if (!open) return;
        dispatch(chatActions.getChatUsers());
        return () => {
            dispatch(chatActions.clearChatUsers());
        };
    }, [open]);

    const handleSearch = useCallback(
        (val) => {
            setSearch(val);
            if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
            searchTimeoutRef.current = setTimeout(() => {
                dispatch(chatActions.getChatUsers({ search: val }, 'noLoad'));
            }, 300);
        },
        [dispatch]
    );

    const toggleUser = (user) => {
        const exists = selectedUsers.find((u) => u.id === user.id && u.type === user.type);
        if (exists) {
            setSelectedUsers(selectedUsers.filter((u) => !(u.id === user.id && u.type === user.type)));
        } else {
            setSelectedUsers([...selectedUsers, user]);
        }
    };

    const handleCreate = () => {
        if (!selectedUsers.length) return;

        const participants = selectedUsers.map((u) => ({
            userId: u.id,
            userType: u.type || CHAT_USER_TYPE.STAFF,
        }));

        if (addToChat) {
            dispatch(chatActions.addChatMembers(addToChat.chatId, participants));
            return;
        }

        const isGroup = selectedUsers.length > 1;
        const body = {
            participants,
            isGroup,
            ...(isGroup && groupName ? { title: groupName } : {}),
        };

        dispatch(chatActions.createChat(body));
    };

    if (!open) return null;

    // Collect user IDs that already have a 1-on-1 (non-group) chat with the current user
    // const existingDirectUserIds = new Set(
    //     (chats || [])
    //         .filter((c) => !c.isGroup)
    //         .flatMap((c) => (c.participants || []).map((p) => p.userId))
    //         .filter((id) => id !== currentUserId)
    // );

    const memberIds = new Set((addToChat?.participants || []).map((p) => p.userId));
    const filteredUsers = (chatUsers || []).filter(
        (u) => u.id !== currentUserId && !memberIds.has(u.id)
        // && !existingDirectUserIds.has(u.id)
    );

    return (
        <div className="chat-modal-overlay" onClick={onClose}>
            <div className="chat-modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="chat-modal-header">
                    <h3>{addToChat ? 'Add Members' : 'New Message'}</h3>
                    <button className="chat-modal-close-btn" onClick={onClose}>
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                            <path
                                d="M15 5L5 15M5 5l10 10"
                                stroke="#667085"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                    </button>
                </div>
                <div className="chat-modal-body">
                    <SingleSearch
                        className={'chat-search-input'}
                        placeholder={'Search for people...'}
                        setSearchTerm={(val) => handleSearch(val)}
                        searchTerm={search}
                    />

                    {/*<input*/}
                    {/*    className="chat-modal-search-input"*/}
                    {/*    placeholder="Search for people..."*/}
                    {/*    value={search}*/}
                    {/*    onChange={(e) => handleSearch(e.target.value)}*/}
                    {/*/>*/}

                    {selectedUsers.length > 0 && (
                        <div className="chat-selected-users">
                            {selectedUsers.map((u) => (
                                <div key={`${u.id}-${u.type}`} className="chat-selected-user-chip">
                                    {u.firstName} {u.lastName}
                                    <button onClick={() => toggleUser(u)}>
                                        <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                                            <path d="M9 3L3 9M3 3l6 6" stroke="#2E90FA" strokeWidth="1.2" strokeLinecap="round" />
                                        </svg>
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}

                    {!addToChat && selectedUsers.length > 1 && (
                        <>
                            <p className="chat-group-name-label">Group Name</p>
                            <input
                                className="chat-group-name-input"
                                placeholder="e.g., Clinical Team, Billing Dept"
                                value={groupName}
                                onChange={(e) => setGroupName(e.target.value)}
                            />
                            <p className="chat-group-name-helper">You're creating a group chat</p>
                        </>
                    )}

                    <div className="chat-user-list">
                        {usersLoading?.length ? (
                            [...Array(5)].map((_, i) => (
                                <div key={i} className="chat-user-skeleton-item">
                                    <div className="chat-user-skeleton-avatar" />
                                    <div className="chat-user-skeleton-info">
                                        <div className="chat-user-skeleton-name" />
                                        <div className="chat-user-skeleton-role" />
                                    </div>
                                </div>
                            ))
                        ) : filteredUsers?.length ? (
                            filteredUsers.map((user) => {
                                const isSelected = selectedUsers.find((u) => u.id === user.id && u.type === user.type);
                                const initials = `${user.firstName?.[0] || ''}${user.lastName?.[0] || ''}`.toUpperCase();

                                return (
                                    <div key={`${user.id}-${user.type}`} className="chat-user-item" onClick={() => toggleUser(user)}>
                                        {user.avatar ? (
                                            <img
                                                src={user.avatar}
                                                alt={`${user.firstName} ${user.lastName}`}
                                                className="chat-user-item-avatar"
                                                style={{ objectFit: 'cover' }}
                                            />
                                        ) : (
                                            <div className="chat-user-item-avatar">{initials}</div>
                                        )}
                                        <div className="chat-user-item-info">
                                            <p>
                                                {user.firstName} {user.lastName}
                                            </p>
                                            <span>{user.type}</span>
                                        </div>
                                        <div className={`chat-user-item-check ${isSelected ? 'chat-user-item-checked' : ''}`}>
                                            {isSelected && (
                                                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                                                    <path
                                                        d="M10 3L4.5 8.5 2 6"
                                                        stroke="#FFFFFF"
                                                        strokeWidth="1.5"
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                    />
                                                </svg>
                                            )}
                                        </div>
                                    </div>
                                );
                            })
                        ) : (
                            <NoYet text="No Such User" position="center" />
                        )}
                    </div>
                </div>
                <div className="chat-modal-footer">
                    <CreateChancel
                        disabled={!selectedUsers.length || !!createLoader?.length}
                        loader={createLoader?.length}
                        create={addToChat ? 'Add' : selectedUsers.length > 1 ? 'Create Group' : 'Start Chat'}
                        chancel={'Cancel'}
                        onCreate={handleCreate}
                        onClose={onClose}
                        buttonWidth="100%"
                    />
                </div>
            </div>
        </div>
    );
};
